<template>
  <section class="customer-profile-data-capture">
    <AM3DataList
      v-model:sort="dataListSort"
      v-model:selected="selectedCampaigns"
      :rows="computedCampaigns"
      :columns="dataListColumns"
      :loading="isFetchingCampaigns && computedCampaigns.length === 0"
      :options="{
        search: 'inside',
        searchPlaceholder: 'Search data captures',
        sortable: true,
        itemVocabulary: ['campaign', 'campaigns'],
        checkboxes: false,
      }"
      row-key="oid"
      :count="campaignListCount"
      data-test-id="customer-campaigns-datalist"
      @row-click="handleDataListRowClick"
      @search="handleDataListSearch"
    >
      <template #after>
        <AM3Pagination
          v-if="totalPages > 1"
          v-model="currentPage"
          :total-pages="totalPages"
          :page-size="pageSize"
          :page-size-options="DATA_CAPTURE_PAGE_SIZE_OPTIONS"
          :loading="isFetchingCampaigns"
          dictionary="Campaigns"
          @change="handlePageChange"
          @update:page-size="handlePageSizeChange"
        />
      </template>

      <!-- Custom cell template for Description with image and campaign name -->
      <template #cell-description="{ row }">
        <div class="flex items-center gap-3 w-full">
          <img
            v-if="row.imageUrl"
            :src="row.imageUrl"
            :alt="row.name"
            loading="lazy"
            class="w-12 h-12 object-cover rounded-lg flex-shrink-0"
          >
          <div
            v-else
            class="w-12 h-12 bg-gray-200 rounded-lg flex items-center justify-center flex-shrink-0"
          >
            <AM3Icon
              name="campaign"
              class="w-6 h-6 text-secondary"
            />
          </div>
          <div class="flex-1 min-w-0">
            <div class="font-medium text-primary">
              {{ row.name }}
            </div>
          </div>
        </div>
      </template>

      <!-- Custom cell template for Type -->
      <template #cell-type="{ row }">
        <span class="capitalize">{{ formatCampaignType(row.type) }}</span>
      </template>

      <!-- Custom cell template for Status -->
      <template #cell-status="{ row }">
        <AM3Tag
          class="capitalize"
          :rounded="false"
          dot
          :type="getCampaignStatusType(row)"
          :label="getCampaignStatusLabel(row)"
        />
      </template>

      <!-- Custom cell template for Referrals -->
      <template #cell-referrals="{ row }">
        <span>{{ (referrals(row.oid) || 0).toLocaleString() }}</span>
      </template>

      <!-- Custom cell template for Points Earned -->
      <template #cell-points="{ row }">
        <span>{{ (campaignPoints(row.oid) || 0).toLocaleString() }}</span>
      </template>

      <template #cell-startDate="{ row }">
        <AM3Formatter
          :value="computeRegistrationTimePerCampaign(row.oid)"
          type="date"
        />
      </template>

      <template #empty>
        <div
          v-if="isFetchingCampaigns"
          class="flex justify-center items-center "
        >
          <AM3Loading
            size="lg"
            color="brand"
          />
          <div class="mt-4 text-center text-lg font-medium">
            Loading campaigns...
          </div>
        </div>

        <div
          v-else
          class=" my-12"
        >
          <AM3EmptyState
            image="EmptyDataCaptures"
            heading="No sign ups yet"
            :description="
              `${customer?.firstName || 'This contact'} has not submitted any forms yet`

            "
          />
        </div>
      </template>
    </AM3DataList>
  </section>
</template>

<script setup lang="ts">
import dayjs from 'dayjs'
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useStore } from 'vuex'
import { dummyFanCampaigns } from '@/utils/audience'
import AM3DataList from '@/components/am3-generic/data/AM3DataList.vue'
import AM3Pagination from '@/components/am3-generic/data/AM3Pagination.vue'
import AM3Tag from '@/components/am3-generic/base/AM3Tag.vue'
import AM3Icon from '@/components/am3-generic/base/icon/AM3Icon.vue'
import AM3Loading from '@/components/am3-generic/base/AM3Loading.vue'
import AM3Formatter from '@/components/am3-generic/data/AM3Formatter.vue'
import AM3EmptyState from '@/components/am3/empty-state/AM3EmptyState.vue'
import { usePaginatedFetch } from '@/composables/usePaginatedFetch'
import type { DataListColumn } from '@/components/am3-generic/data/AM3DataList.vue'
import type { RootState } from '@/store/modules/types'
import type { Sort } from '@/types/sort'

const DATA_CAPTURE_PAGE_SIZE_OPTIONS = [25, 50, 100]

const store = useStore<RootState>()
const router = useRouter()

const searchQueryModel = ref('')
const searchQuery = ref<string | null>(null)
const selectedCampaigns = ref([])
const orderBy = ref({ key: 'startDate', order: 'desc' })
const dataListSort = ref<Sort<any>>({ key: 'startDate', order: 'desc' })

const customerState = computed(() => store.state.customerModule)
const customer = computed(() => store.state.customerModule.customer)
const isFetchingCampaigns = computed(() => store.state.customerModule.isFetchingCampaigns)
const isFeatureEnabled = computed(() => store.getters['authModule/isFeatureEnabled'])

const isCustomerProfileFeatureEnabled = computed(() => isFeatureEnabled.value(['customer-profiles']))

const supportedCampaignStatSortKeys = ['referrals', 'points']

const computedCampaigns = computed(() => {
  if (!isCustomerProfileFeatureEnabled.value) return dummyFanCampaigns()
  return customerState.value.fanCampaigns
})

const campaignListCount = computed(() =>
  customerState.value.customerCampaignCount ?? computedCampaigns.value.length,
)

const dataListColumns: DataListColumn<any>[] = [
  { key: 'description', title: 'Description', freeze: true, width: 476, sortable: false },
  { key: 'type', title: 'Type', width: 120, sortable: true },
  { key: 'status', title: 'Status', width: 120, sortable: false },
  { key: 'referrals', title: 'Referrals', width: 120, sortable: supportedCampaignStatSortKeys.includes('referrals') },
  { key: 'points', title: 'Points earned', width: 150, sortable: supportedCampaignStatSortKeys.includes('points') },
  {
    key: 'startDate',
    title: 'Date signed up',
    width: 180,
    sortable: true,
    format: { type: 'datetime', options: { format: 'MMM D, YYYY' } },
  },
]

onMounted(() => {
  store.commit('customerModule/RESET_CUSTOMER_CAMPAIGNS')
})

onBeforeUnmount(() => {
  store.commit('customerModule/RESET_CUSTOMER_CAMPAIGNS')
})

const {
  currentPage,
  pageSize,
  totalPages,
  fetch: fetchData,
  resetToFirstPage,
  handlePageChange,
  handlePageSizeChange,
} = usePaginatedFetch({
  totalCount: campaignListCount,
  pageSizeOptions: DATA_CAPTURE_PAGE_SIZE_OPTIONS,
  defaultPageSize: 50,
  pageSizeStorageKey: 'ar_customer_data_capture_page_size',
  // FETCH_CUSTOMER_CAMPAIGNS silently drops a call made while one is already in
  // flight (rather than queuing it) - isFetching lets usePaginatedFetch defer and
  // replay instead of firing a call the store would just drop.
  isFetching: isFetchingCampaigns,
  fetchRows: async ({ top, skip }) => {
    if (!customer.value?.oid || !isCustomerProfileFeatureEnabled.value) return

    const orderByValue = `${orderBy.value.key} ${orderBy.value.order}`
    await store.dispatch('customerModule/FETCH_CUSTOMER_CAMPAIGNS', {
      top,
      skip,
      searchString: searchQuery.value,
      orderby: orderByValue,
    })
  },
})

watch(() => customer.value?.oid, (newOid, oldOid) => {
  if (newOid && !oldOid && !isFetchingCampaigns.value && isCustomerProfileFeatureEnabled.value) {
    fetchData()
  }
}, { immediate: true })

watch(dataListSort, (val) => {
  if (val) {
    orderBy.value = { key: val.key, order: val.order }
    resetToFirstPage()
    fetchData()
  }
}, { deep: true })

watch(computedCampaigns, (newCampaigns) => {
  if (newCampaigns.length > 0) {
    fetchAndUpdateCampaignImages()
  }
}, { immediate: true })

async function fetchAndUpdateCampaignImages() {
  const campaigns = computedCampaigns.value

  for (const campaign of campaigns) {
    if (campaign.imageUrl) continue

    let imageUrl = null

    if (campaign.resources?.length > 0) {
      const imageResource = campaign.resources.find((resource: any) => resource.assetType === 'campaign-image')
      if (imageResource) {
        imageUrl = imageResource.url
      }
    }

    campaign.imageUrl = imageUrl
  }
}

function formatCampaignType(type: string) {
  if (!type) return 'Unknown'

  const typeMapping: Record<string, string> = {
    presale: 'Presale',
    waitlist: 'Waitlist',
    signup: 'Sign Up',
    rsvp: 'RSVP',
    contest: 'Contest',
    survey: 'Survey',
  }

  return typeMapping[type.toLowerCase()] || type
}

function isCampaignActive(campaignEndDate: string) {
  return dayjs.utc(campaignEndDate).isAfter(dayjs.utc())
}

function getCampaignStatusType(campaign: any) {
  if (isCampaignActive(campaign.endDate)) {
    return 'active'
  }
  else if (dayjs.utc(campaign.startDate).isAfter(dayjs.utc())) {
    return 'scheduled'
  }
  else {
    return 'completed'
  }
}

function getCampaignStatusLabel(campaign: any) {
  if (isCampaignActive(campaign.endDate)) {
    return 'Live'
  }
  else if (dayjs.utc(campaign.startDate).isAfter(dayjs.utc())) {
    return 'Scheduled'
  }
  else {
    return 'Completed'
  }
}

async function handleDataListSearch(query: string) {
  searchQueryModel.value = query
  searchQuery.value = query
  resetToFirstPage()
  await fetchData()
}

function computeRegistrationTimePerCampaign(campaignOid: number) {
  if (!customer.value?.registrationTimePerCampaign) {
    return null
  }
  return customer.value.registrationTimePerCampaign[campaignOid] || null
}

function campaignPoints(oid: number) {
  return customer.value?.pointsPerCampaign[oid]
}

function referrals(oid: number) {
  return customer.value?.referralsPerCampaign[oid]
}

function handleDataListRowClick(row: any) {
  router.push(`/campaigns/${row.oid}/view/dashboard`)
}
</script>
